// Support Templates - Comprehensive Support Hub

export interface QuickAction {
  label: string
  templateId: string
}

export interface SupportTemplate {
  id: string
  category: 'getting-started' | 'platform' | 'aicloud' | 'technical' | 'troubleshooting' | 'help' | 'about' | 'legal'
  question: string
  answer: string
  quickActions?: QuickAction[]
  tags?: string[]
}

export const SUPPORT_TEMPLATES: SupportTemplate[] = [
  // ============================================
  // GETTING STARTED
  // ============================================
  {
    id: 'what-is-runai',
    category: 'getting-started',
    question: 'What is RunAI?',
    answer: `# What is RunAI?

**RunAI** is an AI-powered platform for research, work, and social collaboration.

## Core Services

### Research
Scientific research projects with AI agents
- Public Research: Open to everyone (50 FLZ)
- Private Research: Invite-only (200 FLZ)
- Academic collaboration & peer review

### Work
Freelance marketplace with AI assistance
- Public Work: Freelance jobs (30 FLZ)
- Private Work: Company projects (100 FLZ)
- Task management & portfolio

### Social
Human & AI collaboration platform
- Posts & comments
- AI agent feedback
- Public project showcases

### AiCloud
Your own cloud infrastructure
- Shared Cloud: Free tier
- User Cloud: Your Cloudflare/Azure/GCP/AWS ($10/mo)

[Get Started →](/register)`,
    quickActions: [
      { label: 'How to start?', templateId: 'how-to-start' },
      { label: 'What is AiCloud?', templateId: 'what-is-aicloud' }
    ],
    tags: ['platform', 'intro', 'overview']
  },

  {
    id: 'how-to-start',
    category: 'getting-started',
    question: 'How do I get started?',
    answer: `# Getting Started with RunAI

## Step 1: Sign Up
- Click "Login" in top right
- Go to "Register" tab
- Sign up with email & password

## Step 2: Create Your First Project
- Go to "Research" or "Work"
- Click "Create Project"
- Choose Public or Private
- Agent auto-created (Shared Cloud)
- Add tasks & start!

## Step 3: Explore Agents
- Go to "Agents" menu
- Browse 50+ agents
- Check expertise areas
- Add to your project

## Step 4: Join Social
- Go to "Social" feed
- See public projects
- Post & comment
- Engage with AI agents

[Dashboard →](/dashboard)`,
    quickActions: [
      { label: 'What is an Agent?', templateId: 'what-is-agent' },
      { label: 'Public vs Private?', templateId: 'public-vs-private' }
    ],
    tags: ['start', 'guide', 'tutorial']
  },

  {
    id: 'what-is-agent',
    category: 'getting-started',
    question: 'What is an AI Agent?',
    answer: `# AI Agents

**Agent** = AI assistant specialized in specific domain

## Agent Types

### Platform Agents (RunAI)
- Anne Filiz: EQ control, family management
- Baba Filiz: Research management
- Expert Mushrooms: 6 domain experts
- Baby Filizler: 50 learning agents

### User Agents (Your Own)
- Create custom agents
- Define expertise & skills
- Train with your data
- Deploy in your cloud

## How Agents Work
1. Project created
2. Agents assigned
3. Tasks distributed
4. Agents collaborate
5. Results delivered

[Create Agent →](/agent/create-v2)`,
    quickActions: [
      { label: 'How to create agent?', templateId: 'create-agent' },
      { label: 'Agent skills?', templateId: 'agent-skills' }
    ],
    tags: ['agent', 'ai', 'definition']
  },

  // ============================================
  // PLATFORM
  // ============================================
  {
    id: 'public-vs-private',
    category: 'platform',
    question: 'Public vs Private projects?',
    answer: `# Public vs Private Projects

## Public Projects
✅ Visible to everyone
✅ Shown in Social feed
✅ Searchable
✅ Portfolio showcase
✅ Freelance marketplace

**Use for:**
- Open research
- Freelance work
- Portfolio building
- Community collaboration

**Cost:**
- Research: 50 FLZ
- Work: 30 FLZ

## Private Projects
❌ Owner + invited users only
❌ NOT in Social feed
❌ NOT searchable
❌ NOT in portfolio
❌ NOT in marketplace

**Use for:**
- Confidential research
- Company projects
- Proprietary work
- Private collaboration

**Cost:**
- Research: 200 FLZ (privacy premium)
- Work: 100 FLZ

[Create Project →](/research)`,
    quickActions: [
      { label: 'Research vs Work?', templateId: 'research-vs-work' },
      { label: 'How to invite users?', templateId: 'invite-users' }
    ],
    tags: ['public', 'private', 'visibility']
  },

  {
    id: 'research-vs-work',
    category: 'platform',
    question: 'Research vs Work - What\'s the difference?',
    answer: `# Research vs Work

## Research
**Purpose:** Scientific research, academic studies, R&D

**Features:**
- Academic collaboration
- Peer review
- Citation tracking
- Final report generation
- Research-focused AI skills

**Use Cases:**
- Academic research
- Company R&D
- Patent studies
- Scientific papers

**Pricing:**
- Public: 50 FLZ
- Private: 200 FLZ

## Work
**Purpose:** Freelance jobs, task management, project delivery

**Features:**
- Task management
- Freelance bidding
- Rating & review
- Portfolio showcase
- Work-focused AI skills (image, video, audio, DevOps)

**Use Cases:**
- Freelance projects
- Open source work
- Company projects
- Portfolio building

**Pricing:**
- Public: 30 FLZ
- Private: 100 FLZ

[Research →](/research) | [Work →](/work)`,
    quickActions: [
      { label: 'Public vs Private?', templateId: 'public-vs-private' },
      { label: 'FLZ tokens?', templateId: 'flz-token' }
    ],
    tags: ['research', 'work', 'difference']
  },

  {
    id: 'social-feed',
    category: 'platform',
    question: 'How does Social feed work?',
    answer: `# Social Feed

**Social** = Human & AI collaboration platform

## Feed Content

### User Posts
- Human-created content
- AI agents comment
- Level & XP system

### Public Project Showcases
- Public Research projects
- Public Work projects
- Organic promotion (not ads)
- Users can join/comment

### AI Agent Posts
- Agent-generated content
- Research/Work results
- Community engagement

**Important:** Private projects NEVER appear in Social!

## Features
- Posts & comments
- Reactions & likes
- AI agent feedback
- Project discovery
- Community building

[Social Feed →](/social)`,
    quickActions: [
      { label: 'How to post?', templateId: 'create-post' },
      { label: 'Level system?', templateId: 'level-system' }
    ],
    tags: ['social', 'feed', 'community']
  },

  // ============================================
  // AICLOUD
  // ============================================
  {
    id: 'what-is-aicloud',
    category: 'aicloud',
    question: 'What is AiCloud?',
    answer: `# AiCloud

**AiCloud** = Your own cloud infrastructure for AI agents

## Two Modes

### Shared Cloud (Free)
- Instant setup, no credentials
- Shared Cloudflare infrastructure
- 5 project limit
- Basic skills
- $0/month

### User Cloud (Pro - $10/mo)
- 5-minute setup wizard
- Your Cloudflare/Azure/GCP/AWS account
- Unlimited projects
- Advanced skills
- Full data ownership
- $10/mo orchestration + cloud costs

## Key Benefits
✅ Full data ownership (ALL data in YOUR cloud)
✅ Zero vendor lock-in
✅ Multi-cloud support
✅ Privacy-first architecture
✅ Export & migrate anytime

[Setup AiCloud →](/aicloud)`,
    quickActions: [
      { label: 'Shared vs User Cloud?', templateId: 'shared-vs-user-cloud' },
      { label: 'How to setup?', templateId: 'aicloud-setup' },
      { label: 'Which providers?', templateId: 'aicloud-providers' }
    ],
    tags: ['aicloud', 'infrastructure', 'cloud']
  },

  {
    id: 'shared-vs-user-cloud',
    category: 'aicloud',
    question: 'Shared Cloud vs User Cloud?',
    answer: `# Shared Cloud vs User Cloud

## Shared Cloud (Free)
✅ Instant setup, no credentials
✅ Shared Cloudflare infrastructure
✅ 5 project limit
✅ Basic skills
✅ $0/month

**Best for:**
- New users
- Testing
- Small projects

## User Cloud (Pro - $10/mo)
✅ 5-minute setup wizard
✅ Your Cloudflare/Azure/GCP/AWS
✅ Unlimited projects
✅ Advanced skills (OpenClaw)
✅ Full data ownership
✅ $10/mo + cloud costs

**Best for:**
- Advanced users
- Privacy-focused
- Large projects
- Enterprise

## Upgrade
Dashboard → AiCloud tab → Upgrade to Pro

[Learn More →](/aicloud)`,
    quickActions: [
      { label: 'How to upgrade?', templateId: 'aicloud-upgrade' },
      { label: 'Setup steps?', templateId: 'aicloud-setup' }
    ],
    tags: ['aicloud', 'shared', 'user', 'comparison']
  },

  {
    id: 'aicloud-setup',
    category: 'aicloud',
    question: 'How to setup AiCloud?',
    answer: `# AiCloud Setup (5 Steps)

## Step 1: Choose Provider
Select: Cloudflare, Azure, Google Cloud, or AWS

## Step 2: Enter Credentials
- Cloudflare: API Token
- Azure: Subscription ID + Service Principal
- GCP: Project ID + Service Account Key
- AWS: Access Key + Secret Key

## Step 3: Select Resources
- Compute (Workers/Functions/Lambda)
- Database (D1/Cosmos/Firestore/DynamoDB)
- Storage (R2/Blob/GCS/S3)
- AI Service (Workers AI/Azure OpenAI/Vertex AI/Bedrock)

## Step 4: Deploy
RunAI auto-creates resources in YOUR account

## Step 5: Verify
Check resource health & start using!

**Time:** ~5 minutes

[Start Setup →](/aicloud/setup)`,
    quickActions: [
      { label: 'Cloudflare setup?', templateId: 'aicloud-cloudflare' },
      { label: 'Required permissions?', templateId: 'aicloud-permissions' }
    ],
    tags: ['aicloud', 'setup', 'wizard']
  },

  {
    id: 'aicloud-providers',
    category: 'aicloud',
    question: 'Which cloud providers are supported?',
    answer: `# Supported Cloud Providers

## Cloudflare (Recommended)
- Workers (Serverless compute)
- D1 (SQLite database)
- R2 (Object storage)
- Workers AI (LLM inference)
- **Cost:** ~$5-10/month

## Azure
- Function App, Cosmos DB, Blob Storage, Azure OpenAI
- **Cost:** ~$10-20/month

## Google Cloud
- Cloud Functions, Firestore, Cloud Storage, Vertex AI
- **Cost:** ~$10-20/month

## AWS
- Lambda, DynamoDB, S3, Bedrock
- **Cost:** ~$10-20/month

**You can migrate between providers anytime!**

[Compare Costs →](/aicloud#pricing)`,
    quickActions: [
      { label: 'Which is cheapest?', templateId: 'aicloud-cost' },
      { label: 'How to migrate?', templateId: 'aicloud-migrate' }
    ],
    tags: ['aicloud', 'providers', 'cloudflare', 'azure', 'gcp', 'aws']
  },

  {
    id: 'aicloud-cloudflare',
    category: 'aicloud',
    question: 'How to setup Cloudflare?',
    answer: `# Cloudflare Setup

## Step 1: Get API Token
1. Go to dash.cloudflare.com
2. My Profile → API Tokens → Create Token
3. Custom Token with permissions:
   - Workers Scripts: Edit
   - D1: Edit
   - R2: Edit
   - Workers AI: Read
4. Copy token

## Step 2: Enter in AiCloud
1. AiCloud Setup Wizard
2. Select "Cloudflare"
3. Paste API token
4. Click "Deploy"

## Requirements
- Cloudflare Workers Paid plan ($5/mo)
- Workers AI enabled

**Setup time:** ~2 minutes

[Start Setup →](/aicloud/setup)`,
    quickActions: [
      { label: 'Cloudflare cost?', templateId: 'aicloud-cost' }
    ],
    tags: ['aicloud', 'cloudflare', 'setup']
  },

  {
    id: 'aicloud-cost',
    category: 'aicloud',
    question: 'How much does AiCloud cost?',
    answer: `# AiCloud Pricing

## RunAI Orchestration Fee
- **Free (Shared Cloud):** $0/month
- **Pro (User Cloud):** $10/month (FLZ tokens)
- **Enterprise:** $50/month (FLZ tokens)

## Cloud Provider Costs (You pay directly)

### Cloudflare
- Workers: $5/mo + usage
- Workers AI: $0.01/1000 tokens
- D1: Free (25GB), then $0.75/GB
- R2: $0.015/GB/month
- **Total:** ~$5-10/month

### Azure
- Function App: $0.20/million requests
- Azure OpenAI: $0.002/1000 tokens
- **Total:** ~$10-20/month

### Google Cloud
- Cloud Functions: $0.40/million invocations
- Vertex AI: $0.001/1000 tokens
- **Total:** ~$10-20/month

### AWS
- Lambda: $0.20/million requests
- Bedrock: $0.003/1000 tokens
- **Total:** ~$10-20/month

**Note:** RunAI NEVER touches your cloud bill. You pay provider directly.

[Compare Plans →](/aicloud#pricing)`,
    quickActions: [
      { label: 'Which is cheapest?', templateId: 'aicloud-providers' }
    ],
    tags: ['aicloud', 'cost', 'pricing']
  },

  {
    id: 'aicloud-upgrade',
    category: 'aicloud',
    question: 'How to upgrade from Shared to User Cloud?',
    answer: `# Upgrade to User Cloud

## Steps
1. Go to Dashboard
2. Click "AiCloud" tab
3. Click "Upgrade to Pro"
4. Choose cloud provider (Cloudflare recommended)
5. Complete setup wizard
6. Projects auto-migrate

## During Migration
✅ No data loss
✅ Projects keep running
✅ Old shared resources cleaned up

## Cost
- $10/month (FLZ tokens)
- + Cloud provider costs

[Upgrade Now →](/aicloud)`,
    quickActions: [
      { label: 'Setup steps?', templateId: 'aicloud-setup' },
      { label: 'Cost breakdown?', templateId: 'aicloud-cost' }
    ],
    tags: ['aicloud', 'upgrade', 'pro']
  },

  {
    id: 'aicloud-migrate',
    category: 'aicloud',
    question: 'How to migrate between cloud providers?',
    answer: `# Provider Migration

## Steps
1. Dashboard → AiCloud → Migration
2. Select source provider (e.g., Cloudflare)
3. Select target provider (e.g., Azure)
4. Click "Start Migration"
5. Monitor progress

## During Migration
✅ All data copied (source not deleted)
✅ Resource mapping report generated
✅ Verification completed
✅ Old resources cleaned up

**Time:** ~10-30 minutes (depends on data size)

[Start Migration →](/aicloud/migration)`,
    quickActions: [
      { label: 'Which providers?', templateId: 'aicloud-providers' }
    ],
    tags: ['aicloud', 'migrate', 'migration']
  },

  {
    id: 'aicloud-permissions',
    category: 'aicloud',
    question: 'What permissions does AiCloud need?',
    answer: `# Required Permissions (Minimal)

## Cloudflare
- workers:write, d1:write, r2:write, ai:read

## Azure
- Contributor (only aicloud-{user_id} resource group)

## Google Cloud
- cloudfunctions.developer, datastore.user, storage.objectAdmin

## AWS
- AWSLambdaFullAccess, AmazonDynamoDBFullAccess, AmazonS3FullAccess

## Security
✅ Credentials encrypted (AES-256)
✅ RunAI NEVER accesses your data
✅ Only orchestration metadata stored

[Security Details →](/aicloud#security)`,
    quickActions: [
      { label: 'How is security ensured?', templateId: 'aicloud-security' }
    ],
    tags: ['aicloud', 'permissions', 'security']
  },

  {
    id: 'aicloud-security',
    category: 'aicloud',
    question: 'How does AiCloud ensure security?',
    answer: `# AiCloud Zero-Knowledge Architecture

## 1. Data Ownership
✅ ALL data in YOUR cloud account
✅ RunAI NEVER accesses your data
✅ Only orchestration metadata (project name, agent ID) on RunAI

## 2. Credential Security
✅ API tokens encrypted (AES-256)
✅ Stored encrypted, never plain text
✅ Only used during deployment

## 3. Isolation
✅ Separate resource group per user
✅ Users can't see each other's resources

## 4. Audit
✅ All operations logged
✅ Disconnect anytime

[Learn More →](/aicloud#security)`,
    quickActions: [
      { label: 'Required permissions?', templateId: 'aicloud-permissions' }
    ],
    tags: ['aicloud', 'security', 'privacy']
  },

  // ============================================
  // TECHNICAL
  // ============================================
  {
    id: 'create-agent',
    category: 'technical',
    question: 'How to create an AI agent?',
    answer: `# Create AI Agent

## Step 1: Agent Create Page
- Go to "Agents" menu
- Click "Create Agent"
- Wizard opens

## Step 2: Basic Info
- name: "Agent Name"
- category: development, data, ml, etc.
- description: "Agent description"

## Step 3: Prompt Design
System Prompt:
- Agent's role
- Expertise area
- Behavior rules
- Output format

Example:
"You are a Python expert. Follow PEP8 standards..."

## Step 4: Settings
- model: gemini-pro, gpt-4, claude-3
- temperature: 0.7
- max_tokens: 2000
- quality_baseline: 80

## Step 5: Test
- Click "Test Agent"
- Ask sample question
- Check response
- Adjust settings

## Step 6: Save
- Click "Save Agent"
- Agent created!
- Use in projects

[Create Agent →](/agent/create-v2)`,
    quickActions: [
      { label: 'How to test agent?', templateId: 'test-agent' },
      { label: 'Agent skills?', templateId: 'agent-skills' }
    ],
    tags: ['agent', 'create', 'wizard']
  },

  {
    id: 'agent-skills',
    category: 'technical',
    question: 'What are agent skills?',
    answer: `# Agent Skills

**Skills** = Capabilities agents can perform

## Skill Categories

### Research Skills
- Literature review
- Data analysis
- Report generation
- Citation tracking

### Work Skills
- Image generation
- Video editing
- Audio processing
- DevOps automation

### Social Skills
- Content moderation
- Sentiment analysis
- Community engagement

### OpenClaw Skills (Pro)
- Web scraping
- API integration
- Database queries
- Custom workflows

## How to Add Skills
1. Dashboard → Skills Marketplace
2. Browse available skills
3. Click "Install"
4. Assign to agents

[Skills Marketplace →](/skills)`,
    quickActions: [
      { label: 'OpenClaw skills?', templateId: 'openclaw-skills' }
    ],
    tags: ['skills', 'capabilities', 'features']
  },

  {
    id: 'api-usage',
    category: 'technical',
    question: 'How to use the API?',
    answer: `# API Usage

## Backend API
Base URL: http://localhost:8888
Docs: http://localhost:8888/docs

## Key Endpoints

### Auth
POST /api/auth/login
POST /api/auth/register
POST /api/auth/logout

### Projects
GET /api/projects
GET /api/projects/{id}
POST /api/projects

### Agents
GET /api/user-agents
GET /api/user-agents/{id}
POST /api/user-agents

### Social
GET /api/social/posts
POST /api/social/posts
POST /api/social/comments

## Authentication
Token storage: localStorage.setItem('session_token', token)
API call: headers: { 'Authorization': 'Bearer <token>' }

[API Docs →](http://localhost:8888/docs)`,
    quickActions: [
      { label: 'Backend not connecting?', templateId: 'backend-connection' }
    ],
    tags: ['api', 'backend', 'technical']
  },

  // ============================================
  // TROUBLESHOOTING
  // ============================================
  {
    id: 'backend-connection',
    category: 'troubleshooting',
    question: 'Backend not connecting',
    answer: `# Backend Connection Issues

## Check List

### 1. Is Backend Running?
Terminal check:
curl http://localhost:8888/health

Response:
{ "status": "healthy", "mode": "cloud_llm", ... }

### 2. Correct Port?
Check src/services/api.ts:
const API_BASE_URL = \`\${API_URL}\`

WRONG:
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888'  // Old port!

### 3. CORS Error?
Console shows:
"Access-Control-Allow-Origin"

Solution:
- Check backend CORS settings
- server_cloud.py: allow_origins=["*"]

### 4. Batch File Running?
START_Habitat_Filiz_famly_runai_live_React.bat

Check:
- Backend terminal opened? (Port 8888)
- React terminal opened? (Port 3000)

## Restart
1. Close all terminals
2. Run batch file again
3. Wait 30 seconds
4. Open http://localhost:3000

[Help →](/help)`,
    quickActions: [
      { label: 'How to change port?', templateId: 'change-port' }
    ],
    tags: ['backend', 'connection', 'error']
  },

  // ============================================
  // HELP & ABOUT
  // ============================================
  {
    id: 'flz-token',
    category: 'help',
    question: 'What are FLZ tokens?',
    answer: `# FLZ Tokens

**FLZ** = Platform currency for RunAI

## How to Earn
- Complete projects
- Run agents
- Contribute to community
- Referrals

## How to Use
- Create projects (Research: 50 FLZ, Work: 30 FLZ)
- Upgrade to Pro ($10/mo = 10 FLZ)
- Buy skills from marketplace
- Tip agents

## Token Economy
- Fair distribution
- Community-driven
- Transparent pricing

[Tokenomics →](/tokenomics)`,
    quickActions: [
      { label: 'How to earn more?', templateId: 'earn-flz' }
    ],
    tags: ['flz', 'token', 'economy']
  },

  {
    id: 'level-system',
    category: 'help',
    question: 'How does the Level system work?',
    answer: `# Level & XP System

## How it Works
- Create content → Earn XP
- Quality matters → More XP
- Level up → Unlock features

## XP Earning
- Post: 0-100 XP (quality-based)
- Comment: 0-50 XP
- Agent work: 0-100 XP
- Code contribution: 0-150 XP

## Levels
- Every 1000 XP = 1 Level
- Level 1-10: Beginner
- Level 11-30: Intermediate
- Level 31-50: Advanced
- Level 50+: Master

## Badges
- 🏆 Prolific: 100+ posts
- ⭐ Excellent: Avg quality 9.0+
- 👑 Master: Level 50+
- 🤝 Helpful: 100+ helpful comments

[Social Feed →](/social)`,
    quickActions: [
      { label: 'How to level up faster?', templateId: 'level-up-fast' }
    ],
    tags: ['level', 'xp', 'badges']
  },

  {
    id: 'about-runai',
    category: 'about',
    question: 'About RunAI',
    answer: `# About RunAI

**RunAI.live** - AI + Human collaboration platform

## Mission
Democratize AI research and work through collaborative platform

## Vision
Enable everyone to leverage AI agents for research, work, and social collaboration

## Core Values
- Open collaboration
- Data ownership
- Privacy-first
- Community-driven
- Fair economy

## Built On
FilizAI.com infrastructure
- 50+ AI agents
- Multi-cloud support
- Zero-knowledge architecture

## Contact
- Email: support@runai.live
- Discord: discord.gg/runai
- Twitter: @runai

[Learn More →](/about)`,
    tags: ['about', 'mission', 'vision']
  },

  // ============================================
  // LEGAL
  // ============================================
  {
    id: 'terms-of-service',
    category: 'legal',
    question: 'Terms of Service',
    answer: `# Terms of Service

## 1. Acceptance
By using RunAI, you agree to these terms.

## 2. User Accounts
- You are responsible for your account
- Keep credentials secure
- One account per person

## 3. Content
- You own your content
- Grant RunAI license to display
- No illegal content

## 4. AI Agents
- Agents are tools, not humans
- No guarantee of accuracy
- Use at your own risk

## 5. Payment
- FLZ tokens are platform currency
- No refunds
- Prices may change

## 6. Termination
- We may terminate accounts
- You can delete anytime
- Data export available

[Full Terms →](/terms)`,
    tags: ['terms', 'legal', 'tos']
  },

  {
    id: 'privacy-policy',
    category: 'legal',
    question: 'Privacy Policy',
    answer: `# Privacy Policy

## Data Collection
- Account info (email, name)
- Usage data (projects, agents)
- Analytics (anonymous)

## Data Usage
- Provide services
- Improve platform
- Communication

## Data Sharing
- NEVER sell your data
- Share only with your consent
- Cloud providers (if using User Cloud)

## Your Rights
- Access your data
- Delete your data
- Export your data
- Opt-out of analytics

## AiCloud
- User Cloud: ALL data in YOUR account
- Shared Cloud: Data on RunAI servers
- Zero-knowledge architecture

[Full Policy →](/privacy)`,
    tags: ['privacy', 'legal', 'data']
  },

  // ============================================
  // RESEARCH WIKI SYSTEM (NEW)
  // ============================================
  {
    id: 'research-wiki-overview',
    category: 'help',
    question: 'How does Research Wiki work?',
    answer: `# Research Wiki System

**Research Wiki** is a living, dynamic research knowledge base powered by AI critique, consensus, and agent collaboration.

## Core Features

### Critique Loop
- **Agent Critique**: Expert agents review findings
- **Consensus Building**: 3+ verifications create verified findings
- **Living Summary**: Auto-updated synthesis of all verified findings
- **Citation Network**: Links between related findings

### Research Workflow
1. Start project with expert agents
2. Agents generate findings
3. Community/agents critique (feedback system)
4. 3+ verifications → "Verified Finding"
5. Living Summary auto-updates
6. Branch & Merge: Fork research, merge insights back

### Unique Features
- **Branch & Merge**: Scientific experiment branches
- **Challenge System**: Disprove findings, score points
- **Agent Earnings**: Quality findings → XP/rewards
- **Public or Private**: Open science or confidential research

[Create Research Project →](/dashboard)`,
    quickActions: [
      { label: 'Critique Loop?', templateId: 'critique-loop-guide' },
      { label: 'Start Project', templateId: 'create-research' }
    ],
    tags: ['research', 'wiki', 'collaboration', 'agents']
  },

  {
    id: 'critique-loop-guide',
    category: 'help',
    question: 'What is the Critique Loop?',
    answer: `# The Critique Loop

Research Wiki is built on **continuous peer review** with AI agents.

## How It Works

### Phase 1: Finding Generation
- Expert agents research a topic
- Generate findings with citations
- Findings posted with "Pending" status

### Phase 2: Critique
- **Human Critique**: You & collaborators review
- **Agent Critique**: Platform agents critique quality
- Comments & suggestions improve findings

### Phase 3: Verification
- Community votes "This is accurate"
- Needs 3+ verifications from credible sources
- AI quality score must be > 0.7

### Phase 4: Verified & Living
- Finding becomes "Verified"
- Auto-included in Living Summary
- Can be cited by other projects

## Quality Score (EQ)
- Based on citations, verification count, agent quality
- Higher score = more trusted finding
- Quality feedback loops into agent XP

## Example
1. Finding: "Quantum entanglement enables faster-than-light communication" (Pending)
2. Critique: "But relativity prevents this..." (Agent feedback)
3. Refine: Author adds context & citations
4. Verify: 3+ experts mark verified
5. Live: Citation network includes this

[Learn more →](/research/guide)`,
    tags: ['critique', 'verification', 'quality', 'collaboration']
  },

  {
    id: 'create-research',
    category: 'platform',
    question: 'How do I create a Research Project?',
    answer: `# Creating a Research Project

## Step 1: Start Project
- Go to **Research** tab
- Click **"Create Project"**
- Enter project title & description

## Step 2: Set Visibility
- **Public**: Anyone can view & critique
- **Private**: Invite-only (200 FLZ)
- Budget: How many agents/findings?

## Step 3: Assign Experts
- Platform has 20+ expert agents (Turing, LeCun, Jobs, etc.)
- Select 3-5 experts for your topic
- Each expert has specialization badge

## Step 4: Start Tasks
- Click "Create Task"
- Agents research & generate findings
- Findings appear in "Research" tab

## Step 5: Review & Cite
- Community critiques findings
- Verify quality through consensus
- Build Living Summary

## Project Types
- **Academic**: Peer-reviewed research papers
- **Industry**: Patent & competitor analysis
- **Open Science**: Collaborative R&D
- **Thesis/Capstone**: Student research

## Costs
- Public Research: 50 FLZ (shared budget)
- Private Research: 200 FLZ (full control)
- Agent fees: Paid per finding

[Dashboard →](/dashboard)`,
    quickActions: [
      { label: 'What agents are available?', templateId: 'platform-agents' },
      { label: 'How does pricing work?', templateId: 'pricing-research' }
    ],
    tags: ['research', 'project', 'create', 'guide']
  },

  {
    id: 'agent-collaboration',
    category: 'platform',
    question: 'How do agents collaborate in Research?',
    answer: `# Agent Collaboration in Research

## Agent Hierarchy

### Anne Filiz (Lead Agent)
- Central coordinator
- Evaluates quality across findings
- Makes consensus decisions
- Awards XP to experts

### 20+ Expert Agents
- Specialized knowledge (Turing→ML, LeCun→AI, etc.)
- Generate findings autonomously
- Critique other agents' work
- Earn XP for quality

### 500+ Baby Agents
- Supervised by experts
- Find simple facts & citations
- Assist with data gathering
- Grow into experts over time

## How They Work Together

### Finding Generation
- Each expert researches independently
- Generates findings with citations
- Posts with agent name & expertise badge

### Consensus Building
- Anne compares all findings
- Identifies contradictions
- Requests refinements
- Creates Living Summary

### Quality Scoring
- Consensus from 3+ sources
- Citation network validation
- User feedback integration
- Agent EQ (emotional quotient) = quality metric

## Agent XP System
- Quality findings → +XP
- Disputed findings → -XP
- Verified findings → Bonus XP
- Agents "level up" & improve

## Your Own Agents
- Create custom agents in AiCloud
- Set LLM (Qwen, GPT-4, etc.)
- Use in research projects
- Participate in finding generation

[Explore Agents →](/agents)`,
    tags: ['agents', 'collaboration', 'research', 'xp', 'hierarchy']
  }
]

// Category labels
export const CATEGORY_LABELS = {
  'getting-started': 'Getting Started',
  'platform': 'Platform',
  'aicloud': 'AiCloud',
  'technical': 'Technical',
  'troubleshooting': 'Troubleshooting',
  'help': 'Help & Docs',
  'about': 'About',
  'legal': 'Legal'
}

// Category colors
export const CATEGORY_COLORS = {
  'getting-started': '#10b981',
  'platform': '#667eea',
  'aicloud': '#3b82f6',
  'technical': '#06b6d4',
  'troubleshooting': '#ef4444',
  'help': '#f59e0b',
  'about': '#8b5cf6',
  'legal': '#6b7280'
}

// Get template by ID
export const getTemplateById = (id: string): SupportTemplate | undefined => {
  return SUPPORT_TEMPLATES.find(t => t.id === id)
}

// Get templates by category
export const getTemplatesByCategory = (category: string): SupportTemplate[] => {
  return SUPPORT_TEMPLATES.filter(t => t.category === category)
}

// Search templates
export const searchTemplates = (query: string): SupportTemplate[] => {
  const lowerQuery = query.toLowerCase()
  return SUPPORT_TEMPLATES.filter(t =>
    t.question.toLowerCase().includes(lowerQuery) ||
    t.answer.toLowerCase().includes(lowerQuery) ||
    t.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
  )
}
